import '@pexip/components/src/fonts.css';
import '@pexip/components/dist/components.css';
import '@pexip/media-components/dist/media-components.css';

import {ErrorBoundary} from '@sentry/react';
import React, {Suspense} from 'react';
import type {Root} from 'react-dom/client';
import {createRoot} from 'react-dom/client';

import {
    FullscreenSpinner,
    type PopOutWindowCloser,
    setPopOutWindowCloser,
} from '@pexip/components';

import './polyfills';

import {App} from './App';
import {applicationConfig} from './applicationConfig';
import {bootstrapBranding} from './branding/bootstrapBranding';
import {config} from './config';
import {NotificationProvider} from './contexts/NotificationProvider';
import {ALLOWED_LANGUAGES, initI18next} from './i18n';
import {logger} from './logger';
import {Error} from './pages/Error.page';
import {router} from './router';
import {setupSentry} from './sentry';
import {callHistoryService} from './services/CallHistory.service';
import {createInfinity} from './services/InfinityClient.service';
import {
    createBrowserNotificationService,
    type NotificationService,
} from './services/Notification.service';
import {registrationService} from './services/Registration.service';
import type {ServiceDiscovery} from './services/ServiceDiscovery.service';
import {
    type SsoTransport,
    setSsoTransport,
} from './services/SsoTransport.service';
import {
    setWindowOpener,
    type WindowOpener,
} from './services/WindowOpener.service';
import {createMeetingLinks} from './utils/createMeetingLinks';

const originalCreateElement = React.createElement;

type CreateElement = typeof originalCreateElement;

React.createElement = ((...args: Parameters<typeof originalCreateElement>) => {
    const props = args[1] as
        (React.Attributes & {'data-testid'?: string}) | null | undefined;
    const testId = props?.['data-testid'];

    if (testId && applicationConfig.hiddenFunctionality[testId]) {
        return originalCreateElement(React.Fragment, null);
    }
    return originalCreateElement(...args);
}) as CreateElement;

let hasInitialized = false;

/**
 * Initializes global Infinity Connect services once for the current document.
 *
 * @example
 * initializeInfinityConnect();
 */
const initializeInfinityConnect = (): void => {
    if (hasInitialized) {
        return;
    }

    setupSentry();
    void initI18next(ALLOWED_LANGUAGES);

    router.urlChangedSignal.add(url => {
        logger.debug({url}, 'Url changed');
    });
    router.urlRequestSignal.add(url => {
        logger.debug({url}, 'request a new Url');
    });

    config.load({}, false);
    hasInitialized = true;
};

/**
 * Host services for {@link mountInfinityConnect}. The web application omits
 * them all.
 *
 * @example
 * mountInfinityConnect(root, {windowOpener: createWindowOpener()});
 */
export interface MountInfinityConnectOptions {
    /** Native-only service discovery; omit it to preserve browser routing. */
    serviceDiscovery?: ServiceDiscovery;
    /** Native-only pop-out closer; omit it to preserve `window.close`. */
    popOutWindowCloser?: PopOutWindowCloser;
    /** Native-only SSO transport; omit it to preserve the browser popup flow. */
    ssoTransport?: SsoTransport;
    /** Native-only notifications; omit it to preserve browser notifications. */
    notificationService?: NotificationService;
    /** Native-only opener for external URLs; omit it to preserve a new tab. */
    windowOpener?: WindowOpener;
}

export type {PopOutWindowCloser} from '@pexip/components';
export type {SSOdMessage} from '@pexip/infinity';

export type {NotificationService} from './services/Notification.service';
export type {
    ServiceDiscovery,
    ServiceDiscoveryErrorCode,
} from './services/ServiceDiscovery.service';
/** Re-exported for native composition without exposing service internals. */
export {
    isServiceDiscoveryErrorCode,
    ServiceDiscoveryError,
} from './services/ServiceDiscovery.service';
export type {
    SsoAbandonReason,
    SsoFlow,
    SsoFlowOptions,
    SsoTransport,
} from './services/SsoTransport.service';
export type {WindowOpener} from './services/WindowOpener.service';

/** The mounted application. */
export interface InfinityConnect {
    /** React root the application renders into. */
    root: Root;
    /**
     * Opens a link to an application route, such as
     * `/m/<alias>?name=<name>&pin=<pin>`, as a page load at that route would.
     *
     * @param route - Route relative to the application base.
     * @returns Whether the link was opened, once it is applied; routes to
     * another origin are refused.
     */
    openLink: (route: string) => Promise<boolean>;
}

/**
 * Mounts Infinity Connect into a web or native-shell document.
 *
 * @param rootElement - Element the application renders into.
 * @param options - Native-only host services.
 * @returns The mounted application.
 *
 * @example
 * const app = mountInfinityConnect(document.getElementById('root'));
 * await app.openLink('/m/meet.alice?name=Bob');
 */
export const mountInfinityConnect = (
    rootElement: HTMLElement | null,
    options: MountInfinityConnectOptions = {},
): InfinityConnect => {
    if (!(rootElement instanceof HTMLElement)) {
        throw new TypeError('Infinity Connect requires an HTML root element');
    }

    initializeInfinityConnect();
    setWindowOpener(options.windowOpener);
    setSsoTransport(options.ssoTransport);
    setPopOutWindowCloser(options.popOutWindowCloser);
    registrationService.setServiceDiscovery(options.serviceDiscovery);
    const infinity = createInfinity({
        serviceDiscovery: options.serviceDiscovery,
    });
    const notifications =
        options.notificationService ?? createBrowserNotificationService();
    // Links are applied once branding is, so their settings override branding
    // defaults, and the application renders only after the page-load link.
    const startup = bootstrapBranding().then(brand => {
        callHistoryService.initFavoritesOrder();
        const meetingLinks = createMeetingLinks(infinity.getClient());
        meetingLinks.openPageLoadLink();
        return {brand, meetingLinks};
    });
    const branding = startup.then(({brand}) => brand);
    const root = createRoot(rootElement);
    root.render(
        <ErrorBoundary
            fallback={({resetError: reload}) => <Error reload={reload} />}
        >
            <NotificationProvider notifications={notifications}>
                <Suspense fallback={<FullscreenSpinner />}>
                    <App infinity={infinity} branding={branding} />
                </Suspense>
            </NotificationProvider>
        </ErrorBoundary>,
    );
    return {
        root,
        openLink: async route => {
            const {meetingLinks} = await startup;
            return meetingLinks.openLink(route);
        },
    };
};
